import React, { Suspense, lazy } from "react";
import { connect } from "react-redux";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import debounce from "lodash/debounce";

import "./assets/css/bootstrap.min.css";
import "./assets/css/paper-kit.min.css";
import "./index.css";

import AccountsAnnounceModal from "./components/AccountsAnnounceModal";
import ErrorBoundary from "./components/ErrorBoundary";
import Notification from "./components/Notification";
import { PageLoader } from "./components/Loaders";
import { toggleUnbounceModal } from "./redux/actions/unBounceModal";
import {
  UNBOUNCE_LOCALSTORAGE_KEY,
  UNBOUNCE_SUBMITED_TIMESTAMP,
  EIGHT_HOURS_MS,
  BROKER_REFERRAL_SESSION_STORAGE_KEY,
} from "./constants";
import { getBrokerByReferral } from "./redux/actions/assessment";
import { COUNTY_SLUGS } from "./constants/county-procedural";
import { captureReferralFromQuery } from "./utils/referralCode";

// Route-level code splitting.  Each container becomes its own
// chunk — the homepage doesn't ship the checkout flow, the FAQ
// page doesn't ship the address-suggestion runtime, etc.  Drops
// initial bundle ~30-50% on most pages and improves Largest
// Contentful Paint, which Google ranks on.
//
// react-snap walks every route in PUBLIC_ROUTES at build time and
// snapshots the rendered HTML.  Each lazy() call becomes its own
// chunk webpack emits alongside the main bundle; the prerendered
// HTML for /faq references the FAQPage chunk by hash, /pricing
// references PricingPage, and so on.  Customers download only
// what their landing route needs, then the Suspense fallback
// covers any subsequent navigation while the next chunk loads.
const LandingPage = lazy(() => import("./containers/LandingPage"));
// ``webpackPrefetch: true`` emits ``<link rel="prefetch">`` for this
// chunk in the entry HTML — every page on the site (homepage, SEO
// landing pages, pricing, FAQ) is one click away from /verify, and
// it's the most-clicked conversion-funnel destination.  Prefetching
// during browser idle keeps the route-change INP off the critical
// path; the chunk is warm whether the user clicks the homepage form
// OR a navbar Link.
const FreeAssessmentPage = lazy(() => import(/* webpackPrefetch: true */ "./containers/FreeAssessmentPage"));
const FAQPage = lazy(() => import("./containers/FAQPage"));
const About = lazy(() => import("./containers/AboutPage"));
const PricingPage = lazy(() => import("./containers/PricingPage"));
const StripeRedirectPage = lazy(() => import("./containers/StripeRedirectPage"));
const PaymentSuccess = lazy(() => import("./containers/PaymentSuccess"));
const CardUpdatedPage = lazy(() => import("./containers/CardUpdatedPage"));
const SubmitAssessment = lazy(() => import("./containers/SubmitAssessment"));
const PreOrderEstimate = lazy(() => import("./containers/PreOrderEstimate"));
const RetrieveReportPage = lazy(() => import("./containers/RetrieveReportPage"));
const PrivacyPolicyPage = lazy(() => import("./containers/PrivacyPolicyPage"));
const SelectReportPage = lazy(() => import("./containers/SelectReportPage"));
const PropertyDossierPage = lazy(() => import("./containers/PropertyDossierPage"));
const TermsAndConditionsPage = lazy(() => import("./containers/TermsAndConditionsPage"));
const RefundPage = lazy(() => import("./containers/RefundPage"));
const RefundStatusPage = lazy(() => import("./containers/RefundStatusPage"));
const CancelPage = lazy(() => import("./containers/CancelPage"));
const ReportLinkPage = lazy(() => import("./containers/ReportLinkPage"));
const SubscriptionConfirmPage = lazy(() => import("./containers/SubscriptionConfirmPage"));
const CountyLandingPage = lazy(() => import("./containers/CountyLandingPage"));
const TexasStateHubPage = lazy(() => import("./containers/TexasStateHubPage"));
const HowToProtestPage = lazy(() => import("./containers/HowToProtestPage"));
const DeadlinesPage = lazy(() => import("./containers/DeadlinesPage"));
const ProtestEvidencePage = lazy(() => import("./containers/ProtestEvidencePage"));
const HomesteadGuidePage = lazy(() => import("./containers/HomesteadGuidePage"));
const GlossaryPage = lazy(() => import("./containers/GlossaryPage"));
const TimelinePage = lazy(() => import("./containers/TimelinePage"));
const CompaniesComparisonPage = lazy(() => import("./containers/CompaniesComparisonPage"));
const ZipLandingPage = lazy(() => import("./containers/ZipLandingPage"));
const AgentsPage = lazy(() => import("./containers/AgentsPage"));
const LoginPage = lazy(() => import("./containers/LoginPage"));
const DashboardPage = lazy(() => import("./containers/DashboardPage"));
const CoveragePage = lazy(() => import("./containers/CoveragePage"));

class App extends React.Component {
  state = {
    mustShowUnbounceModal: false,
  };

  static getDerivedStateFromProps({ isUnbounceModalToggled, isModalSubmitted }) {
    // Wrap every localStorage call — Safari private-browsing /
    // certain enterprise browser policies / quota-exceeded conditions
    // throw on read OR write.  An exception here crashes the whole
    // app on mount because getDerivedStateFromProps fires before any
    // error boundary can catch it.
    let submittedTimestamp = null;
    let isSubmitted = null;
    try {
      submittedTimestamp = localStorage.getItem(UNBOUNCE_SUBMITED_TIMESTAMP);
      if (submittedTimestamp) {
        // Parse explicitly — getItem returns a string and the implicit
        // coercion in (new Date().getTime() - submittedTimestamp) used
        // to silently treat a corrupted/non-numeric value as NaN.
        const ts = parseInt(submittedTimestamp, 10);
        if (Number.isFinite(ts) && new Date().getTime() - ts >= EIGHT_HOURS_MS) {
          localStorage.removeItem(UNBOUNCE_LOCALSTORAGE_KEY);
          localStorage.removeItem(UNBOUNCE_SUBMITED_TIMESTAMP);
        }
      }
      isSubmitted = localStorage.getItem(UNBOUNCE_LOCALSTORAGE_KEY);
    } catch (_e) {
      // localStorage unavailable — degrade gracefully: behave as if
      // the user has never seen the modal.
      isSubmitted = null;
    }

    const mustShowUnbounceModal = !isUnbounceModalToggled && !isSubmitted && !isModalSubmitted;

    return {
      mustShowUnbounceModal,
    };
  }

  toggleModal = debounce(() => {
    this.props.toggleUnbounceModal();
  }, 500);

  componentDidMount() {
    // Native URLSearchParams replaces qs here — saves ~28 KB
    // from the entry chunk (qs + its object-inspect /
    // get-intrinsic transitives) at the cost of dropping
    // features we don't use (nested keys, array brackets,
    // comma-delimited values).  All three call sites for qs
    // in this codebase are flat key=value query strings.
    const params = new URLSearchParams(window.location.search);
    const referral = params.get("referral");
    const storedReferralCode = sessionStorage.getItem(BROKER_REFERRAL_SESSION_STORAGE_KEY);

    if (referral) {
      sessionStorage.setItem(BROKER_REFERRAL_SESSION_STORAGE_KEY, referral);
      window.location.href = window.location.origin + window.location.pathname;
    }

    if (storedReferralCode) {
      this.props.getBrokerByReferral(storedReferralCode);
    }

    // "Share with neighbors" referral code — separate from the
    // broker-referral system above. ?ref=NB-XXXXX gets validated
    // server-side and stashed in localStorage so the discount
    // auto-applies at the email/checkout step further down the
    // funnel. Fire-and-forget — failures are silent (the user
    // can still complete the flow without the discount).
    captureReferralFromQuery();
  }

  render() {
    const { mustShowUnbounceModal } = this.state;

    return (
      <div onMouseLeave={mustShowUnbounceModal ? this.toggleModal : null}>
        <Notification />
        <ErrorBoundary>
        <Router>
          {/* App-wide accounts announcement: existing customers mostly
              arrive via emailed links and SEO pages, not the homepage.
              Self-gated (launch flag, signed-in check, one-time
              localStorage ack, mid-funnel route suppression). */}
          <AccountsAnnounceModal />
          {/* Suspense fallback covers the brief gap while the
              navigated-to chunk loads.  PageLoader matches the
              loading UX customers already see during slow API
              calls — visually consistent. */}
          <Suspense fallback={<PageLoader text="Loading…" />}>
          <Switch>
            <Route path="/pre-order/submit" exact>
              <PreOrderEstimate />
            </Route>
            <Route path="/select-report/:orderUuid" exact>
              <SelectReportPage />
            </Route>
            <Route path="/property-dossier/:orderUuid" exact>
              <PropertyDossierPage />
            </Route>
            <Route path="/submit">
              <SubmitAssessment />
            </Route>
            <Route path={"/verify"}>
              <FreeAssessmentPage
                isUnbounceModalToggled={this.props.isUnbounceModalToggled}
                isModalSubmitted={this.props.isModalSubmitted}
                toggleUnbounceModal={this.props.toggleUnbounceModal}
              />
            </Route>
            <Route path="/subscribe/:orderUuid" exact>
              <StripeRedirectPage subscribe />
            </Route>
            <Route path="/checkout/:orderUuid" exact>
              <StripeRedirectPage />
            </Route>
            <Route path="/report/:uuid">
              <RetrieveReportPage />
            </Route>
            <Route path={"/faq"}>
              <FAQPage />
            </Route>
            <Route path="/about">
              <About />
            </Route>
            <Route path="/pricing">
              <PricingPage />
            </Route>
            {/* Realtor-focused partner landing page — separate
                audience and CTA from the homeowner homepage. */}
            <Route path="/agents" exact>
              <AgentsPage />
            </Route>
            {/* Passwordless customer accounts. /login is noindex (no SEO
                value); /dashboard is private (token-gated, never indexed
                or prerendered). */}
            <Route path="/login" exact>
              <LoginPage />
            </Route>
            <Route path="/dashboard" exact>
              <DashboardPage />
            </Route>
            {/* Address-coverage fallback landing — reached when a user
                picks an address we don't serve yet. noIndex (only
                meaningful mid-flow with a selected address in state). */}
            <Route path="/coverage" exact>
              <CoveragePage />
            </Route>
            <Route path="/privacy" exact>
              <PrivacyPolicyPage />
            </Route>
            <Route path="/terms-and-conditions" exact>
              <TermsAndConditionsPage />
            </Route>
            <Route path="/refund/:orderUuid/status" exact>
              <RefundStatusPage />
            </Route>
            <Route path="/refund" exact>
              <RefundPage />
            </Route>
            <Route path="/cancel" exact>
              <CancelPage />
            </Route>
            {/* Landing page for expired/invalid signed report links —
                emails redirect here with ?order=<uuid>&reason=. */}
            <Route path="/report-link" exact>
              <ReportLinkPage />
            </Route>
            {/* Guest cancel/skip confirmation — emailed link with
                ?token=. Explicit button click confirms (never
                auto-submitted; email scanners prefetch links). */}
            <Route path="/subscription/confirm" exact>
              <SubscriptionConfirmPage />
            </Route>
            <Route path="/success">
              <PaymentSuccess />
            </Route>
            <Route path="/card-updated">
              <CardUpdatedPage />
            </Route>
            <Route path="/preorder-complete">
              <PreOrderEstimate preorderComplete />
            </Route>

            {/* SEO landing pages.  ``:countySlug`` matches the slug
                in src/constants/county-procedural.js and is bound
                to a single CountyLandingPage component that
                dispatches on the slug.  Order matters: more
                specific routes first so the React Router fallthrough
                doesn't catch them. */}
            <Route path="/texas-property-tax-protest" exact>
              <TexasStateHubPage />
            </Route>
            <Route path="/how-to-protest-property-taxes" exact>
              <HowToProtestPage />
            </Route>
            <Route path="/property-tax-protest-deadlines-2026" exact>
              <DeadlinesPage />
            </Route>
            <Route path="/property-tax-protest-evidence" exact>
              <ProtestEvidencePage />
            </Route>
            <Route path="/texas-homestead-exemption-guide" exact>
              <HomesteadGuidePage />
            </Route>
            <Route path="/property-tax-glossary" exact>
              <GlossaryPage />
            </Route>
            <Route path="/texas-property-tax-timeline-2026" exact>
              <TimelinePage />
            </Route>
            <Route path="/property-tax-protest-companies-comparison" exact>
              <CompaniesComparisonPage />
            </Route>
            {/* One Route per county.  Each path is a static
                slug pattern matching county-procedural.js;
                useParams in CountyLandingPage reads the slug
                back from the path via the captured ``:countySlug``
                parameter.  Static enumeration (rather than a
                regex) means react-router-v5 routes cleanly and
                react-snap can crawl each path explicitly. */}
            {COUNTY_SLUGS.map((slug) => (
              <Route
                key={slug}
                path={`/${slug}-county-property-tax-protest`}
                exact
                render={() => <CountyLandingPage countySlug={slug} />}
              />
            ))}

            {/* Per-zip-code landing pages.  The path-to-regexp
                pattern ``\d{5}`` only matches 5-digit zip codes, so
                ``/abc-property-tax-protest`` falls through to the
                catch-all redirect.  ZipLandingPage returns null
                for any zip not in county-stats.json — pages don't
                ship until the ETL extract has data for them. */}
            <Route
              path="/:zipCode(\d{5})-property-tax-protest"
              exact
              render={({ match }) => (
                <ZipLandingPage zipCode={match.params.zipCode} />
              )}
            />

            <Route path="/" exact>
              <LandingPage />
            </Route>
            <Route render={() => <Redirect to="/" />} />
          </Switch>
          </Suspense>
        </Router>
        </ErrorBoundary>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  isUnbounceModalToggled: state.unBounceModal.unbounceModalShow,
  isModalSubmitted: state.unBounceModal.isSubmitted,
});

const mapDispatchToProps = (dispatch) => ({
  toggleUnbounceModal: () => dispatch(toggleUnbounceModal()),
  getBrokerByReferral: (referral) => dispatch(getBrokerByReferral(referral)),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
